import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, ChevronDown, MapPin, Users, Link2, X } from 'lucide-react';
import { CustomDatePicker } from '../../ui/CustomDatePicker';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { formatDateES } from '../../../lib/dateUtils';
import { ReunionOption } from '../../../lib/api/reuniones';
import { logger } from '../../../lib/logger';

/**
 * Shared form field components used across entity modals
 */

// Municipality Dropdown Field
interface MunicipalityFieldProps {
  value: string;
  onChange: (value: string) => void;
  municipalities: string[];
  isEditing: boolean;
  required?: boolean;
  accentColor?: string;
}

export const MunicipalityField: React.FC<MunicipalityFieldProps> = ({
  value,
  onChange,
  municipalities,
  isEditing,
  required = true,
  accentColor = '#5D1828',
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useClickOutside<HTMLDivElement>(() => setIsDropdownOpen(false));

  const filteredMunicipalities = municipalities.filter(m =>
    m.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">
        Municipio {required && <span className="text-[#5D1828]">*</span>}
      </label>
      {isEditing ? (
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={`w-full flex items-center justify-between px-4 py-3 bg-white border rounded-lg text-left transition-all ${
              isDropdownOpen ? `border-[${accentColor}] ring-1 ring-[${accentColor}]` : 'border-[#D1D5DB] hover:border-[#9CA3AF]'
            }`}
            style={isDropdownOpen ? { borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` } : undefined}
          >
            <div className="flex items-center gap-2">
              <MapPin size={18} className="text-[#6B7280]" />
              <span className={`text-sm ${value ? 'text-[#111827] font-medium' : 'text-[#6B7280]'}`}>
                {value || 'Seleccionar...'}
              </span>
            </div>
            <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          <AnimatePresence>
            {isDropdownOpen && dropdownRef.current && (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 5 }}
                style={{
                  position: 'fixed',
                  top: dropdownRef.current.getBoundingClientRect().bottom + 8,
                  left: dropdownRef.current.getBoundingClientRect().left,
                  width: dropdownRef.current.getBoundingClientRect().width,
                }}
                className="bg-white rounded-lg shadow-xl border border-[#D1D5DB] overflow-hidden z-[200] max-h-60 flex flex-col"
              >
                <div className="p-2 border-b border-[#E5E7EB] bg-[#F9FAFB]">
                  <input
                    type="text"
                    autoFocus
                    placeholder="Buscar..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full bg-white border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm outline-none"
                    style={{ borderColor: search ? accentColor : undefined }}
                  />
                </div>
                <div className="overflow-y-auto p-1">
                  {filteredMunicipalities.map((city) => (
                    <button
                      key={city}
                      type="button"
                      onClick={() => {
                        onChange(city);
                        setIsDropdownOpen(false);
                        setSearch('');
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        value === city
                          ? 'font-medium'
                          : 'text-[#4B5563] hover:bg-[#F3F4F6]'
                      }`}
                      style={value === city ? { backgroundColor: `${accentColor}10`, color: accentColor } : undefined}
                    >
                      {city}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ) : (
        <div className="text-[#111827] font-medium flex items-center gap-2 p-3 bg-[#F9FAFB] rounded-lg border border-[#D1D5DB]">
          <MapPin size={18} className="text-[#6B7280]" />
          {value}
        </div>
      )}
    </div>
  );
};

// Date Picker Field
interface DateFieldProps {
  value: string;
  onChange: (value: string) => void;
  isEditing: boolean;
  label?: string;
  required?: boolean;
  accentColor?: string;
}

export const DateField: React.FC<DateFieldProps> = ({
  value,
  onChange,
  isEditing,
  label = 'Fecha de Solicitud',
  required = true,
  accentColor = '#5D1828',
}) => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const datePickerRef = useClickOutside<HTMLDivElement>(() => setIsDatePickerOpen(false));

  const getFormattedDate = (dateString: string) => {
    if (!dateString) return '';
    return formatDateES(dateString, { format: 'short', includeWeekday: true });
  };

  return (
    <div>
      <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">
        {label} {required && <span className="text-[#5D1828]">*</span>}
      </label>
      {isEditing ? (
        <div className="relative" ref={datePickerRef}>
          <button
            type="button"
            onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
            className={`relative w-full py-3 px-4 rounded-lg border-2 border-dashed transition-all flex items-center justify-center gap-3 group select-none ${
              value
                ? 'bg-opacity-5 border-opacity-30'
                : 'bg-[#F9FAFB] border-[#D1D5DB] hover:bg-white text-[#6B7280]'
            }`}
            style={
              value
                ? {
                    backgroundColor: `${accentColor}0D`,
                    borderColor: `${accentColor}4D`,
                    color: accentColor,
                  }
                : undefined
            }
          >
            <Calendar size={20} style={value ? { color: accentColor } : undefined} />
            <span className={`font-medium text-sm ${value ? 'text-[#111827]' : ''}`}>
              {value ? getFormattedDate(value) : 'Seleccionar fecha'}
            </span>
          </button>

          <AnimatePresence>
            {isDatePickerOpen && (
              <CustomDatePicker
                selectedDate={value}
                onChange={onChange}
                onClose={() => setIsDatePickerOpen(false)}
                parentRef={datePickerRef}
              />
            )}
          </AnimatePresence>
        </div>
      ) : (
        <div className="text-[#111827] font-medium flex items-center gap-2 p-3 bg-[#F9FAFB] rounded-lg border border-[#D1D5DB]">
          <Calendar size={18} className="text-[#6B7280]" />
          {getFormattedDate(value)}
        </div>
      )}
    </div>
  );
};

// Priority Selector Field
interface PriorityFieldProps {
  value: 'Alta' | 'Media' | 'Baja' | null;
  onChange: (value: 'Alta' | 'Media' | 'Baja' | null) => void;
  isEditing: boolean;
}

export const PriorityField: React.FC<PriorityFieldProps> = ({ value, onChange, isEditing }) => {
  return (
    <div>
      <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">
        Prioridad (Opcional)
      </label>
      {isEditing ? (
        <div>
          <div className="flex items-center gap-2">
            {(['Alta', 'Media', 'Baja'] as const).map((level) => (
              <button
                key={level}
                type="button"
                onClick={() => onChange(level)}
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all border ${
                  value === level
                    ? level === 'Alta'
                      ? 'bg-red-100 border-red-300 text-red-700'
                      : level === 'Media'
                        ? 'bg-amber-100 border-amber-300 text-amber-700'
                        : 'bg-green-100 border-green-300 text-green-700'
                    : 'bg-white border-[#D1D5DB] text-[#4B5563] hover:bg-[#F3F4F6]'
                }`}
              >
                {level}
              </button>
            ))}
          </div>
          {value && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="mt-2 text-xs text-[#6B7280] hover:text-[#5D1828]"
            >
              Limpiar prioridad
            </button>
          )}
        </div>
      ) : (
        <div
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
            value === 'Alta'
              ? 'bg-red-100 text-red-700 border border-red-200'
              : value === 'Media'
                ? 'bg-amber-100 text-amber-700 border border-amber-200'
                : value === 'Baja'
                  ? 'bg-green-100 text-green-700 border border-green-200'
                  : 'bg-[#F9FAFB] text-[#9CA3AF] border border-[#D1D5DB]'
          }`}
        >
          {value || 'Sin prioridad asignada'}
        </div>
      )}
    </div>
  );
};

// Beneficiaries Field
interface BeneficiariesFieldProps {
  value: number | null;
  onChange: (value: number | null) => void;
  isEditing: boolean;
}

export const BeneficiariesField: React.FC<BeneficiariesFieldProps> = ({ value, onChange, isEditing }) => {
  return (
    <div>
      <label className="block text-xs font-bold text-[#4B5563] uppercase tracking-wide mb-2">
        Número de Beneficiarios (Opcional)
      </label>
      {isEditing ? (
        <div>
          <div className="relative">
            <Users size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B7280]" />
            <input
              type="number"
              min="0"
              step="1"
              value={value ?? ''}
              onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : null)}
              placeholder="Ej: 50"
              className="w-full pl-11 pr-4 py-3 bg-white border border-[#D1D5DB] rounded-lg text-[#111827] text-sm outline-none focus:border-[#5D1828] focus:ring-1 focus:ring-[#5D1828] transition-all placeholder:text-[#9CA3AF]"
            />
          </div>
          <p className="text-xs text-[#9CA3AF] mt-1">Estimado de personas beneficiadas</p>
        </div>
      ) : (
        <div className="text-[#111827] font-medium flex items-center gap-2 p-3 bg-[#F9FAFB] rounded-lg border border-[#D1D5DB]">
          <Users size={18} className="text-[#6B7280]" />
          {value !== null && value !== undefined ? value : 'Sin especificar'}
        </div>
      )}
    </div>
  );
};

// Reunion Assignment Field (for Propuestas/Gestiones)
interface ReunionAssignmentFieldProps {
  isAssigned: boolean;
  onToggle: (value: boolean) => void;
  selectedReunionId: string | null;
  onSelectReunion: (id: string | null) => void;
  reunionOptions: ReunionOption[];
  reunionOptionsLoading: boolean;
  isEditing: boolean;
  accentColor?: string;
  preselectedTitle?: string;
}

export const ReunionAssignmentField: React.FC<ReunionAssignmentFieldProps> = ({
  isAssigned,
  onToggle,
  selectedReunionId,
  onSelectReunion,
  reunionOptions,
  reunionOptionsLoading,
  isEditing,
  accentColor = '#5D1828',
  preselectedTitle,
}) => {
  const [isReunionDropdownOpen, setIsReunionDropdownOpen] = useState(false);
  const [reunionSearch, setReunionSearch] = useState('');
  const reunionDropdownRef = useClickOutside<HTMLDivElement>(() => setIsReunionDropdownOpen(false));

  const filteredReuniones = reunionOptions.filter(
    (r) => r.title.toLowerCase().includes(reunionSearch.toLowerCase()) || r.date.includes(reunionSearch)
  );

  const selectedReunion = reunionOptions.find((r) => r.id === selectedReunionId);
  const displayTitle = selectedReunion?.title || preselectedTitle || 'Reunión vinculada';

  return (
    <div className="col-span-2">
      <div
        onClick={() => isEditing && onToggle(!isAssigned)}
        className={`flex items-center justify-between bg-[#F9FAFB] p-4 rounded-lg border border-[#D1D5DB] ${
          isEditing ? 'cursor-pointer group hover:bg-[#F3F4F6]' : ''
        } transition-colors`}
        style={
          isAssigned
            ? {
                borderColor: `${accentColor}80`,
                backgroundColor: `${accentColor}0D`,
                boxShadow: `0 0 0 1px ${accentColor}`,
              }
            : undefined
        }
      >
        <div>
          <h4 className={`font-semibold text-sm`} style={isAssigned ? { color: accentColor } : undefined}>
            Asignar a Reunión
          </h4>
          <p className="text-xs text-[#6B7280]">¿Esta propuesta/gestión surgió durante una reunión?</p>
        </div>
        <div className="relative">
          <div className={`w-12 h-7 rounded-full transition-colors duration-300 ease-in-out`} style={{ backgroundColor: isAssigned ? accentColor : '#D1D5DB' }} />
          <div
            className={`absolute top-1 left-1 bg-white w-5 h-5 rounded-full shadow-md transform transition-transform duration-300 ease-in-out ${
              isAssigned ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </div>
      </div>

      <AnimatePresence>
        {isAssigned && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: 'auto', marginTop: 12 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            className="overflow-hidden"
          >
            {isEditing ? (
              <div className="relative" ref={reunionDropdownRef}>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setIsReunionDropdownOpen(!isReunionDropdownOpen)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsReunionDropdownOpen(!isReunionDropdownOpen);
                    }
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 bg-white border rounded-lg text-left transition-all cursor-pointer`}
                  style={
                    isReunionDropdownOpen
                      ? { borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }
                      : { borderColor: '#D1D5DB' }
                  }
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Link2 size={18} className="text-[#6B7280] shrink-0" />
                    <span className={`text-sm truncate ${selectedReunionId ? 'text-[#111827] font-medium' : 'text-[#6B7280]'}`}>
                      {selectedReunionId ? displayTitle : 'Seleccionar reunión...'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {selectedReunionId && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectReunion(null);
                          setReunionSearch('');
                        }}
                        className="p-1 hover:bg-gray-100 rounded-full text-[#6B7280] hover:text-[#DC2626]"
                      >
                        <X size={14} />
                      </button>
                    )}
                    <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${isReunionDropdownOpen ? 'rotate-180' : ''}`} />
                  </div>
                </div>

                <AnimatePresence>
                  {isReunionDropdownOpen && reunionDropdownRef.current && (
                    <motion.div
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 5 }}
                      style={{
                        position: 'fixed',
                        top: reunionDropdownRef.current.getBoundingClientRect().bottom + 8,
                        left: reunionDropdownRef.current.getBoundingClientRect().left,
                        width: reunionDropdownRef.current.getBoundingClientRect().width,
                      }}
                      className="bg-white rounded-lg shadow-xl border border-[#D1D5DB] overflow-hidden z-[200] max-h-60 flex flex-col"
                    >
                      <div className="p-2 border-b border-[#E5E7EB] bg-[#F9FAFB]">
                        <input
                          type="text"
                          autoFocus
                          placeholder="Buscar reunión..."
                          value={reunionSearch}
                          onChange={(e) => setReunionSearch(e.target.value)}
                          className="w-full bg-white border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm outline-none"
                          style={{ borderColor: reunionSearch ? accentColor : undefined }}
                        />
                      </div>
                      <div className="overflow-y-auto p-1">
                        {reunionOptionsLoading ? (
                          <div className="px-3 py-4 text-sm text-[#6B7280] text-center">Cargando reuniones...</div>
                        ) : filteredReuniones.length === 0 ? (
                          <div className="px-3 py-4 text-sm text-[#6B7280] text-center">No se encontraron reuniones</div>
                        ) : (
                          filteredReuniones.map((reunion) => (
                            <button
                              key={reunion.id}
                              type="button"
                              onClick={() => {
                                logger.log('[ReunionAssignmentField] Selection clicked, calling onSelectReunion with:', reunion.id);
                                onSelectReunion(reunion.id);
                                setReunionSearch('');
                                setIsReunionDropdownOpen(false);
                              }}
                              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                selectedReunionId === reunion.id ? 'font-medium' : 'text-[#4B5563] hover:bg-[#F3F4F6]'
                              }`}
                              style={
                                selectedReunionId === reunion.id
                                  ? { backgroundColor: `${accentColor}10`, color: accentColor }
                                  : undefined
                              }
                            >
                              <div className="font-medium truncate">{reunion.title}</div>
                              <div className="text-xs text-[#9CA3AF] mt-0.5">
                                {formatDateES(reunion.date, { format: 'short' })}
                                {reunion.municipality_name && ` · ${reunion.municipality_name}`}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="text-[#111827] font-medium flex items-center gap-2 p-3 bg-[#F9FAFB] rounded-lg border border-[#D1D5DB]">
                <Link2 size={18} className="text-[#6B7280]" />
                {displayTitle}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
